import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { useCallback } from 'react';
import { catalog } from '@atlassiansox/metal-client';
import { internalGasV3TrackEventChannel, internalMetalChannel, internalSentryExceptionChannel } from '../channels';
import { GUARANTEED, MAYBE, NONE } from '../probability-of-bug';
import { withTelemetryInfoScopeContext } from '../sentry/tags';
import { UNKNOWN } from '../service';
import { responsibleServiceIncludesCommerceLibraries } from '../service/utils';
// TODO: This probably doesn't belong here. Move.
export var capitalize = function capitalize(actionSubject) {
  return actionSubject.charAt(0).toUpperCase() + actionSubject.substring(1);
};

/**
 * This is a lower level API version of {@link useTaskTracker}. Use this if the higher level hook does not
 * suit your needs. The disadvantage of using this version of the hook is that it is technically possible to fail
 * or succeed a task more than once, which, generally is not desireable.
 */
export var useBaseTaskTracker = function useBaseTaskTracker(_ref) {
  var action = _ref.action,
      actionSubject = _ref.actionSubject,
      _ref$histogramBuckets = _ref.histogramBuckets,
      histogramBuckets = _ref$histogramBuckets === void 0 ? '4000' : _ref$histogramBuckets;
  var dispatchGasV3Track = internalGasV3TrackEventChannel.useBreadcrumbedEventDispatch();
  var dispatchSentry = internalSentryExceptionChannel.useBreadcrumbedEventDispatch();
  var dispatchMetal = internalMetalChannel.useBreadcrumbedEventDispatch();
  var task = "".concat(action).concat(capitalize(actionSubject));
  return useCallback(function (payload) {
    var startTime = Date.now();

    var sharedGasV3Payload = _objectSpread({
      action: action,
      actionSubject: actionSubject
    }, payload);

    var sharedMetalInfo = {
      task: task
    };

    var sendSentryEvent = function sendSentryEvent(loggablePayload, metalName, metalTaskId, exceptionTags) {
      if (loggablePayload === undefined && [MAYBE, NONE].includes(exceptionTags.probabilityOfBug)) {
        // Don't bother sending a payload if it's undefined and the probability of bug isn't guaranteed
        // TODO: Should enforce that it's never undefined for ProbabilityOfBug.GUARANTEED
        return;
      }

      dispatchSentry({
        exception: loggablePayload,
        scopeContext: withTelemetryInfoScopeContext({
          tags: _objectSpread(_objectSpread(_objectSpread({
            task: task,
            metalName: metalName
          }, exceptionTags), metalTaskId !== undefined ? {
            metalTaskId: metalTaskId
          } : undefined), payload.actionSubjectId !== undefined ? {
            actionSubjectId: payload.actionSubjectId
          } : undefined)
        })
      });
    };

    var sendMetalEvent = function sendMetalEvent(metalName, metalTaskId) {
      dispatchMetal(_objectSpread(_objectSpread(_objectSpread({
        name: catalog.userInteraction.TASK_DURATION
      }, metalTaskId === undefined ? null : {
        taskId: metalTaskId
      }), sharedMetalInfo), {}, {
        value: Date.now() - startTime,
        histogramBuckets: histogramBuckets
      }));
      dispatchMetal(_objectSpread(_objectSpread(_objectSpread({
        name: metalName
      }, metalTaskId === undefined ? null : {
        taskId: metalTaskId
      }), sharedMetalInfo), {}, {
        value: 1
      }));
    };

    var fail = function fail() {
      var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          loggablePayload = _ref2.loggablePayload,
          _ref2$probabilityOfBu = _ref2.probabilityOfBug,
          probabilityOfBug = _ref2$probabilityOfBu === void 0 ? MAYBE : _ref2$probabilityOfBu,
          baseMetalTaskId = _ref2.metalTaskId,
          _ref2$responsibleServ = _ref2.responsibleService,
          responsibleService = _ref2$responsibleServ === void 0 ? UNKNOWN : _ref2$responsibleServ;

      var metalTaskId = function () {
        if (baseMetalTaskId !== undefined) {
          return baseMetalTaskId;
        }

        if (probabilityOfBug === GUARANTEED) {
          if (responsibleServiceIncludesCommerceLibraries(responsibleService)) {
            return 'guaranteed-bug';
          }

          return 'external-bug';
        } // Not sending any other values in fear of cardinality problems


        return undefined;
      }();

      sendMetalEvent(catalog.userInteraction.TASK_FAILURE, metalTaskId);
      sendSentryEvent(loggablePayload, catalog.userInteraction.TASK_FAILURE, metalTaskId, {
        probabilityOfBug: probabilityOfBug,
        responsibleService: responsibleService
      }); // TODO: Consider sending some sort of GasV3 failure information
    };

    var succeed = function succeed() {
      var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          loggablePayload = _ref3.loggablePayload,
          metalTaskId = _ref3.metalTaskId,
          _ref3$responsibleServ = _ref3.responsibleService,
          responsibleService = _ref3$responsibleServ === void 0 ? UNKNOWN : _ref3$responsibleServ;

      sendMetalEvent(catalog.userInteraction.TASK_SUCCESS, metalTaskId);
      sendSentryEvent(loggablePayload, catalog.userInteraction.TASK_SUCCESS, metalTaskId, {
        probabilityOfBug: NONE,
        responsibleService: responsibleService
      });
      dispatchGasV3Track(sharedGasV3Payload);
    };

    return {
      fail: fail,
      succeed: succeed
    };
  }, [dispatchGasV3Track, dispatchMetal, dispatchSentry, task, histogramBuckets, action, actionSubject]);
};
export var isSuccessfulTaskResult = function isSuccessfulTaskResult(x) {
  return x.succeed !== undefined;
};
/**
 * This hook is a high level hook designed to make it easy to send telemetry related to
 * running processes in response to a user action. We call this a "task" (a definition taken from Metal).
 *
 * Depending on what you provide to this hook, it will send to:
 *  - GasV3
 *  - Metal
 *  - Sentry
 *
 * @param taskInfo Inputs that define the task. Should generally speaking be pretty static.
 * Having these fields here also makes debugging what a given task tracker sends at runtime easier.
 *
 * @returns A function that records the executes a task, measuring its duration and tracking whether it suceeds or fails.
 *
 * @see {@link useBaseTaskTracker} A lower level version of this hook. Disadvantage is that it's possible to fail/suceed more than once in the lower level API.
 */

export var useTaskTracker = function useTaskTracker(taskInfo) {
  var startTask = useBaseTaskTracker(taskInfo);
  var higherLevelStartTask = useCallback(
  /*#__PURE__*/

  /**
   * Please note that thrown errors will be interpreted as guaranteed buggy failures.
   * @param payload This data passed into this function will be shared between both failure and successful tasks
   */
  function () {
    var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(callback) {
      var payload,
          _startTask,
          fail,
          succeed,
          result,
          _args = arguments;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              payload = _args.length > 1 && _args[1] !== undefined ? _args[1] : {};
              _startTask = startTask(payload), fail = _startTask.fail, succeed = _startTask.succeed;
              _context.prev = 2;
              _context.next = 5;
              return callback();

            case 5:
              result = _context.sent;

              if (result.succeed !== undefined) {
                succeed(result.succeed);
              } else if (result.fail !== undefined) {
                fail(result.fail);
              } else {
                fail({
                  probabilityOfBug: GUARANTEED,
                  loggablePayload: {
                    result: result,
                    error: new Error('Could not find succeed nor fail')
                  }
                });
              }

              return _context.abrupt("return", result);

            case 10:
              _context.prev = 10;
              _context.t0 = _context["catch"](2);

              if (_context.t0 instanceof TypeError && _context.t0.message === 'Failed to fetch') {
                fail({
                  probabilityOfBug: MAYBE,
                  loggablePayload: _context.t0
                });
              } else {
                fail({
                  probabilityOfBug: GUARANTEED,
                  loggablePayload: _context.t0
                });
              }

              throw _context.t0;

            case 14:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[2, 10]]);
    }));

    return function (_x) {
      return _ref4.apply(this, arguments);
    };
  }(), [startTask]);
  return higherLevelStartTask;
};